import React, { useState } from 'react';
import { ArrowRight, CheckCircle, Mail, Star, Zap, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import { pushToDataLayer } from '../lib/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

const PRODUCTS_PREVIEW = [
  {
    name: 'HYROX Race Day Tank',
    tagline: 'Moisture-wicking. Race tested.',
    accent: '#E63946',
  },
  {
    name: 'Station Grind Hoodie',
    tagline: 'Warmup to cooldown comfort.',
    accent: '#c8ff00',
  },
  {
    name: 'Sled Push Compression Shorts',
    tagline: 'Built for functional movement.',
    accent: '#00F0FF',
  },
  {
    name: 'HYROX Finisher Tee',
    tagline: 'Earn it. Wear it.',
    accent: '#E63946',
  },
];

export default function ComingSoonPage() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [size, setSize] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, already, error
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    setErrorMsg('');
    try {
      const res = await fetch(`${API_URL}/api/preorder/notify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name, product_line: 'hyrox-apparel', size_interest: size }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      setStatus(data.status === 'already_registered' ? 'already' : 'success');
      if (data.status !== 'already_registered') {
        pushToDataLayer('waitlist_join', {
          waitlist: 'hyrox_apparel_preorder',
          product_line: 'hyrox-apparel',
          size_interest: size || '',
          source: 'coming-soon-page',
        });
      }
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="coming-soon-page">
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-zinc-900 via-[#0a0a0a] to-[#0a0a0a]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#E63946]/5 rounded-full blur-[120px]" />

        <div className="relative max-w-4xl mx-auto px-4 pt-20 pb-16 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#c8ff00]/10 border border-[#c8ff00]/20 mb-8">
            <Zap className="w-3.5 h-3.5 text-[#c8ff00]" />
            <span className="text-[#c8ff00] text-xs font-bold uppercase tracking-[3px]">Coming Soon</span>
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-none mb-6">
            HYROX<br />
            <span className="text-[#E63946]">PERFORMANCE</span><br />
            APPAREL
          </h1>

          <p className="text-zinc-400 text-base sm:text-lg max-w-lg mx-auto leading-relaxed mb-12">
            Engineered for the stations. Designed for the grind. Our new HYROX apparel line drops soon &mdash; get exclusive early access.
          </p>

          {/* Signup Form */}
          {status === 'success' || status === 'already' ? (
            <div className="max-w-md mx-auto bg-zinc-900/60 border border-[#c8ff00]/20 p-8" data-testid="preorder-success">
              <CheckCircle className="w-12 h-12 text-[#c8ff00] mx-auto mb-4" />
              <h3 className="text-white text-xl font-bold mb-2">
                {status === 'already' ? "You're Already On The List!" : "You're In!"}
              </h3>
              <p className="text-zinc-400 text-sm">
                {status === 'already'
                  ? "We've already got you registered. Stay tuned for the launch."
                  : "We'll email you first when the collection drops, with exclusive early-access pricing."}
              </p>
              <Link to="/shop" className="inline-flex items-center gap-2 mt-6 px-6 py-2.5 bg-[#E63946] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#c41e3a]"
                data-testid="shop-current-link">
                Shop Current Collection <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="max-w-md mx-auto space-y-4" data-testid="preorder-form">
              <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Your name (optional)"
                className="w-full bg-zinc-900 border border-zinc-800 text-white px-4 py-3 text-sm focus:border-[#c8ff00] focus:outline-none placeholder:text-zinc-700"
                data-testid="preorder-name" />
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Your email address" required
                className="w-full bg-zinc-900 border border-zinc-800 text-white px-4 py-3 text-sm focus:border-[#c8ff00] focus:outline-none placeholder:text-zinc-700"
                data-testid="preorder-email" />

              <div>
                <p className="text-zinc-500 text-xs uppercase tracking-wider mb-2 text-left">Preferred Size</p>
                <div className="flex gap-2">
                  {SIZES.map(s => (
                    <button key={s} type="button" onClick={() => setSize(size === s ? '' : s)}
                      className={`flex-1 py-2 text-xs font-bold border transition-colors ${size === s
                        ? 'border-[#c8ff00] bg-[#c8ff00]/10 text-[#c8ff00]'
                        : 'border-zinc-800 text-zinc-600 hover:border-zinc-600'}`}
                      data-testid={`size-${s}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {errorMsg && <p className="text-red-400 text-xs text-left">{errorMsg}</p>}

              <button type="submit" disabled={status === 'loading'}
                className="w-full bg-[#c8ff00] text-black py-3.5 font-black text-sm uppercase tracking-wider hover:bg-[#d4ff33] disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="preorder-submit">
                {status === 'loading' ? (
                  <span className="animate-pulse">Registering...</span>
                ) : (
                  <><Mail className="w-4 h-4" /> Get Early Access</>
                )}
              </button>
              <p className="text-zinc-600 text-xs">No spam. Just launch day access + exclusive pricing.</p>
            </form>
          )}
        </div>
      </div>

      {/* Product Preview Grid */}
      <div className="max-w-5xl mx-auto px-4 py-16">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-1.5 h-8 bg-[#E63946]" />
          <span className="text-zinc-500 text-xs font-bold uppercase tracking-[4px]">Preview</span>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          {PRODUCTS_PREVIEW.map((p, i) => (
            <div key={p.name} className="group relative bg-zinc-900/60 border border-zinc-800 p-8 hover:border-zinc-700 transition-colors"
              data-testid={`preview-product-${i}`}>
              <div className="absolute top-4 right-4 px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase border" style={{ borderColor: `${p.accent}33`, color: p.accent }}>
                SOON
              </div>
              <div className="w-16 h-16 border-2 flex items-center justify-center mb-6" style={{ borderColor: `${p.accent}40` }}>
                <Star className="w-6 h-6" style={{ color: p.accent }} />
              </div>
              <h3 className="text-white font-bold text-lg mb-1">{p.name}</h3>
              <p className="text-zinc-500 text-sm">{p.tagline}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Benefits */}
      <div className="border-t border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 py-16">
          <div className="grid sm:grid-cols-3 gap-8 text-center">
            {[
              { icon: Zap, color: '#c8ff00', title: 'Early Access', desc: 'Subscribers get 24h head start before public launch' },
              { icon: Shield, color: '#E63946', title: 'Exclusive Pricing', desc: 'Pre-launch pricing only available to waitlist members' },
              { icon: Star, color: '#00F0FF', title: 'Limited Runs', desc: 'Small batch production. Once sold out, it\'s gone.' },
            ].map((b, i) => (
              <div key={b.title}>
                <b.icon className="w-8 h-8 mx-auto mb-3" style={{ color: b.color }} />
                <h4 className="text-white font-bold text-sm mb-1">{b.title}</h4>
                <p className="text-zinc-500 text-xs leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
